import React, { forwardRef, useState, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Controller, useForm } from 'react-hook-form';

import { required } from '../../utils/form-default-errors';
import { updateUser } from '../../app/slices/user/userSlice';
import { updateUserProfile } from '../../services/users';
import { uploadProfilePicture } from '../../services/media';
import { errorHandler } from '../../services/authService';
import { TEACHER_ROLES_OPTIONS } from '../../../constants/general.constants';
import { Avatar, Button, IconButton, Select, TextInput, Typography } from '../';
import * as S from './EditTeacherProfileModal.styles';
import { fetchSchools } from '../../services/school';

export const EditTeacherProfileModal = forwardRef(({ user, dispatch, currentUser = false, allowedEditUID = false }, ref) => {
	const [isOpen, setIsOpen] = useState(false);
	const [avatar, setAvatar] = useState(null);
	const [avatarFile, setAvatarFile] = useState(null);
	const [schools, setSchools] = useState([]);
	const [isLoadingSchools, setIsLoadingSchools] = useState(false);

	const {
		handleSubmit,
		control,
		setValue,
		register,
		watch,
		getValues,
		formState: { errors, isSubmitting },
	} = useForm();

	const getSchoolId = (school) => school?.id || school?.objectId || school?.schoolId || null;

	const normalizeSchool = (school) => {
		const id = getSchoolId(school);
		if (id == null) return null;
		return school.id ? school : { ...school, id };
	};

	const dedupeSchoolOptions = (list = []) => {
		const seen = new Set();
		const unique = [];
		list.forEach((school) => {
			const normalized = normalizeSchool(school);
			if (normalized != null && !seen.has(normalized.id)) {
				seen.add(normalized.id);
				unique.push(normalized);
			}
		});
		return unique;
	};

	const ensurePrimarySelection = (options) => {
		if (options == null || options.length === 0) return;
		const currentId = getValues('primarySchoolId');
		const hasSelection = options.some((school) => getSchoolId(school) === currentId);
		if (!hasSelection) {
			setValue('primarySchoolId', getSchoolId(options[0]));
		}
	};

	const primarySchoolId = watch('primarySchoolId');
	const selectedSchool = schools?.find((school) => getSchoolId(school) === primarySchoolId);

	const loadSchools = async () => {
		setIsLoadingSchools(true);
		try {
			const {
				data: { result },
			} = await fetchSchools({ page: 0, isSlim: true });
			const fetchedSchools = dedupeSchoolOptions(result?.schools || []);
			setSchools((prev) => {
				const nextOptions = dedupeSchoolOptions([...(prev || []), ...fetchedSchools]);
				ensurePrimarySelection(nextOptions);
				return nextOptions;
			});
		} catch (error) {
			errorHandler(error);
			setSchools((prev) => {
				if (prev != null && prev.length > 0) return prev;
				const fallbackOptions = dedupeSchoolOptions([user?.primarySchool]);
				ensurePrimarySelection(fallbackOptions);
				return fallbackOptions;
			});
		} finally {
			setIsLoadingSchools(false);
		}
	};

	const handleOpenModal = () => {
		setValue('roleName', user?.teacherRole);
		setValue('primarySchoolId', getSchoolId(user?.primarySchool) || null);
		setValue('uid', user?.uid);
		const initialSchools = dedupeSchoolOptions([user?.primarySchool, ...(user?.schools || [])]);
		ensurePrimarySelection(initialSchools);
		setSchools(initialSchools);
		setAvatar(user?.profileImageObj?.[200]);
		setAvatarFile(null);
		setIsOpen(true);
		loadSchools();
	};

	const handleCloseModal = () => {
		setIsOpen(false);
	};

	async function asyncCaller(data) {
		let profileImageObj;

		if (avatarFile) {
			const formData = new FormData();
			formData.append('file', avatarFile);

			const { data: uploadResult } = await uploadProfilePicture(formData);

			profileImageObj = uploadResult;
		} else {
			profileImageObj = user?.profileImageObj;
		}

		const {
			data: { result },
		} = await updateUserProfile({ ...data, userId: user.id, profileImageObj });

		if (currentUser) {
			dispatch(updateUser(result?.user));
		} else {
			dispatch({ type: 'SET_USER', payload: result?.user });
		}
		handleCloseModal();
	}

	const onSubmit = (e) => {
		handleSubmit(asyncCaller)(e).catch(errorHandler);
	};

	useImperativeHandle(
		ref,
		() => ({
			open: handleOpenModal,
			close: handleCloseModal,
		}),
		[handleOpenModal, handleCloseModal]
	);

	return (
		<ReactModal className="profile-card-modal" ref={ref} closeTimeoutMS={200} isOpen={isOpen} onRequestClose={handleCloseModal}>
			<S.Form onSubmit={onSubmit}>
				<IconButton className="close-button" type="button" icon={['fal', 'times']} onClick={handleCloseModal} />
				<div className="modal-content">
					<header className="modal-content__header">
						<Typography tag="h2" weight="extrablack" center>
							Edit Profile
						</Typography>
						<Typography tag="p" center>
							Please update your profile information here.
						</Typography>
					</header>

					<section className="modal-content__section">
						<Avatar className="avatar-element" avatar={avatar} setAvatar={setAvatar} file={avatarFile} setFile={setAvatarFile} />

						<TextInput
							label="Teacher UID"
							placeholder="Teacher UID"
							error={errors.uid}
							{...register('uid', {
								required: required('Teacher UID'),
							})}
							name="uid"
							readOnly={!allowedEditUID}
						/>

						<Controller name="roleName" control={control} render={({ field: { onChange, value } }) => <Select label="Role" id="role-name" options={TEACHER_ROLES_OPTIONS} value={TEACHER_ROLES_OPTIONS.find((c) => c.value === value) || ''} onChange={(val) => onChange(val.value)} />} />

						<div>
							<Controller
								name="primarySchoolId"
								control={control}
								rules={{ required: required('School') }}
								render={({ field: { onChange, value }, fieldState: { error: primarySchoolError } }) => (
									<Select
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => getSchoolId(option)}
										label="School"
										id="primary-school-id"
										options={schools}
										promise={{
											function: fetchSchools,
											params: { isSlim: true },
											key: 'schools',
											setOptions: (optionsList) => {
												setSchools((prev) => {
													const nextOptions = dedupeSchoolOptions([...(prev || []), ...(optionsList || [])]);
													ensurePrimarySelection(nextOptions);
													return nextOptions;
												});
											},
										}}
										isLoading={isLoadingSchools}
										value={schools?.find((c) => getSchoolId(c) === value) || null}
										onChange={(val) => onChange(getSchoolId(val))}
										error={primarySchoolError}
									/>
								)}
							/>

							<div className="information-card">
								<div className="icon-wrapper">
									<FontAwesomeIcon icon="school" />
								</div>
								<div className="info-wrapper">
									<Typography tag="h5" weight="bold">
										{selectedSchool?.name}
									</Typography>
									<div className="info-line">
										<FontAwesomeIcon icon={['fal', 'map-marker']} />
										<Typography tag="p" variation="2">
											{`${selectedSchool?.address?.addressLine1}, ${selectedSchool?.address?.state} ${selectedSchool?.address?.postalCode}`}
										</Typography>
									</div>
									<div className="info-line">
										<FontAwesomeIcon icon={['fal', 'landmark']} />
										<Typography tag="p" variation="2">
											{selectedSchool?.address?.city}
										</Typography>
									</div>
									<div className="info-line">
										<FontAwesomeIcon icon={['fal', 'school']} />
										<Typography tag="p" variation="2">
											{selectedSchool?.schoolCode}
										</Typography>
									</div>
								</div>
							</div>
						</div>
					</section>

					<div className="modal-footer">
						<Button disabled={isSubmitting || isLoadingSchools}>
							<Typography variation="button-medium" weight="bold">
								{isSubmitting || isLoadingSchools ? 'Loading' : 'Save'}
							</Typography>
						</Button>
					</div>
				</div>
			</S.Form>
		</ReactModal>
	);
});

EditTeacherProfileModal.displayName = 'EditTeacherProfileModal';

EditTeacherProfileModal.propTypes = {
	className: PropTypes.string,
	user: PropTypes.shape(),
	dispatch: PropTypes.func,
	currentUser: PropTypes.bool,
	allowedEditUID: PropTypes.bool,
};
